<template>
    <div>
        <div
            :class="containerClass"
            class="position-relative bg-white"
            style="height: 52px"
            @click="!disabled && $emit('click')"
        >
            <input
                ref="input"
                type="number"
                class="bg-transparent autofill-light transition-duration-25 transition-ease-out outline-none border-0 text-dark placeholder-gray-dark w-100 font--16 lh--20"
                :class="inputClass"
                style="
                    transition-property: opacity, padding-bottom, padding-top;
                "
                :autocomplete="autocomplete"
                :name="name"
                :placeholder="cookedPlaceholder"
                :disabled="disabled"
                :readonly="readonly"
                :value="value"
                :min="min"
                :step="step"
                @input="input"
                @focus="focus"
                @blur="blur"
            />

            <div
                v-if="cookedInnerLabel"
                class="position-absolute top left transition-ease-out transition-duration-25 text-gray-dark pointer-events-none"
                :class="labelClass"
                style="
                    transition-property: font-size, padding-top, padding-bottom;
                "
            >
                {{ cookedInnerLabel }}
            </div>
        </div>

        <input-status :status="status" />
    </div>
</template>

<script>
import { computed } from "vue"
import { slotHasContent } from "~/lib/vueUtils"
import InputStatus from "~/components/inputs/InputStatus"

export default {
    props: {
        ...InputStatus.props,

        currency: {
            type: String,
            default: "€",
        },

        min: {
            type: [Number, String],
            default: 0,
        },

        step: {
            type: [Number, String],
            default: 0.01,
        },

        isFocused: {
            type: Boolean,
            default: false,
        },

        readonly: {
            type: Boolean,
            default: false,
        },

        disabled: {
            type: Boolean,
            default: false,
        },

        name: {
            type: String,
            default: null,
        },

        placeholder: {
            type: String,
            default: "",
        },

        innerLabel: {
            type: String,
            default: "",
        },

        autocomplete: {
            type: String,
            default: "off",
        },

        value: {
            type: Number,
            default: null,
        },

        borderless: {
            type: Boolean,
            default: false,
        },

        rounded: {
            type: Boolean,
            default: true,
        },
    },

    emits: ["click", "update:value", "focus", "blur", "mounted"],

    setup(_props, { slots }) {
        const hasLeftSlot = computed(() => slotHasContent(slots.left))
        const hasRightSlot = computed(() => slotHasContent(slots.right))
        return { hasLeftSlot, hasRightSlot }
    },

    data() {
        return {
            focused: false,
        }
    },

    computed: {
        containerClass() {
            const borderPrimary = this.$css(this.$theme.borderPrimary)
            const status = this.status.code
            return {
                "hover:border-gray-dark": !this.disabled && !status,
                [borderPrimary]: this.focused,
                border: !this.borderless,
                "border-gray-medium": !this.borderless && !status,
                "rounded-lg": this.rounded,
                "border-danger": status == "danger",
                "border-info": status == "info",
                "border-success": status == "success",
                "border-warning": status == "warning",
                "opacity--50": this.disabled,
            }
        },

        labelClass() {
            return {
                "font--16 lh--20 py-15": !(this.focused || this.value),
                "font--12 lh--16 pt-8": this.focused || this.value,

                "pl-44": this.hasLeftSlot,
                "pr-44": this.hasRightSlot,

                "pl-12": !this.hasLeftSlot,
                "pr-12": !this.hasRightSlot,
            }
        },

        inputClass() {
            return {
                "opacity--0": this.innerLabel && !(this.focused || this.value),
                "opacity--100": this.innerLabel && (this.focused || this.value),
                "py-15": !this.innerLabel,
                "pt-22 pb-8": this.innerLabel,

                "pl-44": this.hasLeftSlot,
                "pr-44": this.hasRightSlot,

                "pl-12": !this.hasLeftSlot,
                "pr-12": !this.hasRightSlot,
            }
        },

        cookedPlaceholder() {
            const placeholder = this.placeholder
                ? `${this.placeholder} (${this.currency})`
                : ""
            if (!this.cookedInnerLabel) {
                return placeholder
            }

            if (this.focused) {
                return placeholder
            } else {
                return ""
            }
        },

        cookedInnerLabel() {
            if (!this.innerLabel) return ""
            return `${this.innerLabel} (${this.currency})`
        },
    },

    watch: {
        isFocused() {
            this.isFocused ? this.$refs.input.focus() : this.$refs.input.blur()
        },
    },

    mounted() {
        if (this.$refs.input == document.activeElement) {
            this.focused = true
        }
        this.$emit("mounted")
    },

    methods: {
        setFocus() {
            this.$refs.input.focus()
        },

        input(event) {
            const value = Number(event.target.value)
            this.$emit("update:value", value)
        },

        focus() {
            this.focused = true
            this.$emit("focus")
        },

        blur() {
            this.focused = false
            this.$emit("blur")
        },
    },

    components: {
        InputStatus,
    },
}
</script>

<style scoped>
.py-15 {
    padding-top: 15px !important;
    padding-bottom: 15px !important;
}

/* Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

/* Firefox */
input[type="number"] {
    -moz-appearance: textfield;
}
</style>
